import { useState, useId, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import { CreditCard as CreditCardIcon, Loader2 } from 'lucide-react';
import {
  CARD_BRAND_LABELS,
  mapSquareCardBrand,
} from '../../utils/cardBrand';

/** Default — bordered fields for checkout / profile flows. */
const SQUARE_CARD_ENTRY_STYLE = {
  '.input-container': {
    borderColor: '#d1d5db',
    borderRadius: '6px',
    borderWidth: '1px',
  },
  '.input-container.is-focus': {
    borderColor: '#171717',
    borderWidth: '1px',
  },
  '.input-container.is-error': {
    borderColor: '#dc2626',
    borderWidth: '1px',
  },
  input: {
    fontSize: '16px',
    color: '#111827',
  },
  'input::placeholder': {
    color: '#9ca3af',
  },
  'input.is-error': {
    color: '#111827',
  },
};

/** Partner portal — fields inline; outer partner-card-box is the only visible border. */
const PARTNER_SQUARE_CARD_STYLE = {
  '.input-container': {
    borderColor: 'transparent',
    borderRadius: 0,
    borderWidth: '1px',
    boxShadow: 'none',
  },
  '.input-container.is-focus': {
    borderColor: 'transparent',
    borderWidth: '1px',
    boxShadow: 'none',
  },
  '.input-container.is-error': {
    borderColor: '#dc2626',
    borderWidth: '1px',
    borderRadius: '4px',
  },
  input: {
    fontSize: '15px',
    color: '#111827',
    backgroundColor: 'transparent',
  },
  'input::placeholder': {
    color: '#9ca3af',
  },
  'input.is-error': {
    color: '#dc2626',
  },
  '.message-text': {
    color: 'transparent',
    fontSize: '0',
    lineHeight: '0',
    margin: 0,
    padding: 0,
  },
  '.message-icon': {
    display: 'none',
  },
};

/**
 * Square Web Payments — card data tokenized in Square iframe (PCI-safe).
 * Submit button and optional footer render inside PaymentForm (SDK requirement).
 */
export default function SquareCardEntry({
  applicationId,
  locationId,
  postalCode = '',
  submitLabel,
  submittingLabel = 'Processing…',
  payHint = '',
  disabled = false,
  onToken,
  onError,
  buttonClassName = 'interested-step2-primary',
  externalSubmitContainer = null,
  variant = 'default',
  children = null,
}) {
  const [detectedBrand, setDetectedBrand] = useState(null);
  const cardContainerId = useId().replace(/:/g, '');
  const hostRef = useRef(null);
  const recalculateSizeRef = useRef(null);
  const normalizedPostalCode = String(postalCode || '').replace(/\D/g, '').slice(0, 10);

  useEffect(() => {
    if (!hostRef.current) return undefined;

    const recalculate = () => recalculateSizeRef.current?.();
    const t1 = setTimeout(recalculate, 0);
    const t2 = setTimeout(recalculate, 200);
    const t3 = setTimeout(recalculate, 600);

    const observer = new ResizeObserver(recalculate);
    observer.observe(hostRef.current);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      observer.disconnect();
    };
  }, [applicationId, locationId, cardContainerId]);

  const squareCallbacks = useMemo(
    () => ({
      cardBrandChanged(event) {
        const brand = mapSquareCardBrand(event?.detail?.cardBrand);
        setDetectedBrand(brand);
      },
    }),
    []
  );

  if (!applicationId || !locationId) {
    return (
      <p className="interested-step2-square-unavailable">
        Secure card entry is not configured. Contact support.
      </p>
    );
  }

  const cardStyle = variant === 'partner' ? PARTNER_SQUARE_CARD_STYLE : SQUARE_CARD_ENTRY_STYLE;

  return (
    <div className={`square-card-entry${variant === 'partner' ? ' square-card-entry--partner' : ''}`}>
      <PaymentForm
        applicationId={applicationId}
        locationId={locationId}
        cardTokenizeResponseReceived={async (tokenResult) => {
          try {
            if (tokenResult.status !== 'OK' || !tokenResult.token) {
              onError(tokenResult.errors?.[0]?.message || 'Could not verify card. Try again.');
              return;
            }
            await onToken(tokenResult.token);
          } catch (err) {
            onError(err.message || 'Payment failed');
          }
        }}
      >
        <div
          ref={hostRef}
          className={`interested-step2-field interested-step2-field--card-number${
            variant === 'partner' ? ' partner-square-card-host' : ''
          }`}
        >
          <CreditCard
            id={cardContainerId}
            className="square-card-entry-host"
            style={cardStyle}
            includeInputLabels={variant !== 'partner'}
            postalCode={variant === 'partner' ? undefined : normalizedPostalCode || undefined}
            callbacks={squareCallbacks}
            recalculateSize={(fn) => {
              recalculateSizeRef.current = fn;
            }}
            render={(Button) => {
              const submitButton = (
                <Button
                  className={buttonClassName}
                  isLoading={disabled}
                  css={
                    externalSubmitContainer
                      ? {
                          width: 'auto',
                          minHeight: '2.75rem',
                          padding: '0.75rem 1.35rem',
                          background: 'var(--partner-orange, #ea580c)',
                          color: '#fff',
                          fontSize: '0.9375rem',
                          fontWeight: 700,
                          border: 'none',
                          borderRadius: '10px',
                          boxShadow: 'none',
                          '&:hover': { background: 'var(--partner-orange-hover, #c2410c)' },
                          '&:disabled': { opacity: 0.55, cursor: 'not-allowed' },
                        }
                      : {
                          width: '100%',
                          minHeight: '2.75rem',
                          padding: '0.625rem 1rem',
                          background: '#171717',
                          color: '#fff',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          border: 'none',
                          borderRadius: '6px',
                          boxShadow: 'none',
                          '&:hover': { background: '#262626' },
                          '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
                        }
                  }
                >
                  {disabled ? (
                    <span className="interested-step2-primary-inner">
                      <Loader2 size={18} className="interested-step2-spin" /> {submittingLabel}
                    </span>
                  ) : (
                    <span className="interested-step2-primary-inner">
                      <CreditCardIcon size={18} /> {submitLabel}
                    </span>
                  )}
                </Button>
              );

              return (
                <>
                  {children}
                  {externalSubmitContainer
                    ? createPortal(submitButton, externalSubmitContainer)
                    : submitButton}
                  {payHint ? <p className="interested-step2-pay-hint">{payHint}</p> : null}
                </>
              );
            }}
          />
          {detectedBrand && variant !== 'partner' && (
            <p className="square-card-entry-brand-hint">
              {CARD_BRAND_LABELS[detectedBrand] || detectedBrand} recognized
            </p>
          )}
        </div>
      </PaymentForm>
    </div>
  );
}
